import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Cog, Clock, Euro, Shield, Wrench, Plus, Minus } from 'lucide-react'

import { useForm } from '../../hooks/useForm'
import { useToast } from '../Toast'
import Button from '../ui/Button'
import Input from '../ui/Input'
import Select from '../ui/Select'
import TextArea from '../ui/Textarea'

import servicosService, { ServicoFormData } from '../../services/servicos'
import { Servico } from '../../types'

interface ServicoFormProps {
  servico?: Servico
  onSuccess: () => void
  onCancel: () => void
}

const ServicoForm: React.FC<ServicoFormProps> = ({
  servico,
  onSuccess,
  onCancel
}) => {
  const { success, error } = useToast()
  
  // Estados locais
  const [equipamentos, setEquipamentos] = useState<string[]>(
    servico?.equipamentosSuportados || ['']
  )

  // Query para obter categorias
  const { data: categorias } = useQuery({
    queryKey: ['servicos-categorias'],
    queryFn: () => servicosService.getCategorias()
  })

  // Form
  const {
    values,
    errors,
    isSubmitting,
    handleSubmit,
    getFieldProps,
    setFieldValue
  } = useForm({
    initialValues: {
      nome: servico?.nome || '',
      descricao: servico?.descricao || '',
      categoria: servico?.categoria || 'reparacao',
      preco: servico?.preco || 0,
      tempoEstimado: servico?.tempoEstimado || '3-5 dias úteis',
      garantia: servico?.garantia || '3 meses',
      observacoes: servico?.observacoes || '',
      ativo: servico?.ativo ?? true
    },
    validationRules: {
      nome: {
        required: 'Nome é obrigatório',
        minLength: { value: 3, message: 'Nome deve ter pelo menos 3 caracteres' }
      },
      categoria: {
        required: 'Categoria é obrigatória'
      },
      preco: {
        required: 'Preço é obrigatório',
        min: { value: 0, message: 'Preço deve ser maior ou igual a zero' }
      },
      tempoEstimado: {
        required: 'Tempo estimado é obrigatório'
      },
      garantia: {
        required: 'Garantia é obrigatória'
      }
    },
    onSubmit: async (formData) => {
      try {
        const servicoData: ServicoFormData = {
          ...formData,
          equipamentosSuportados: equipamentos.filter(e => e.trim() !== '')
        }

        if (servico) {
          await servicosService.update(servico.id, servicoData)
          success('Serviço atualizado com sucesso!')
        } else {
          await servicosService.create(servicoData)
          success('Serviço criado com sucesso!')
        }
        
        onSuccess()
      } catch (err: any) {
        error(err.message || 'Erro ao salvar serviço')
      }
    }
  })

  // Handlers para equipamentos suportados
  const adicionarEquipamento = () => {
    setEquipamentos([...equipamentos, ''])
  }

  const removerEquipamento = (index: number) => {
    if (equipamentos.length > 1) {
      setEquipamentos(equipamentos.filter((_, i) => i !== index))
    }
  }

  const atualizarEquipamento = (index: number, valor: string) => {
    const novosEquipamentos = [...equipamentos]
    novosEquipamentos[index] = valor
    setEquipamentos(novosEquipamentos)
  }

  // Opções de categoria
  const categoriasOptions = [
    { value: 'ecra', label: 'Ecrã' },
    { value: 'bateria', label: 'Bateria' },
    { value: 'upgrade', label: 'Upgrade' },
    { value: 'carregamento', label: 'Carregamento' },
    { value: 'reparacao', label: 'Reparação' },
    { value: 'manutencao', label: 'Manutenção' }
  ]

  // Opções de tempo estimado
  const temposOptions = [
    { value: '1 hora', label: '1 hora' },
    { value: '2-3 horas', label: '2-3 horas' },
    { value: '1 dia útil', label: '1 dia útil' },
    { value: '2-3 dias úteis', label: '2-3 dias úteis' },
    { value: '3-5 dias úteis', label: '3-5 dias úteis' },
    { value: '1 semana', label: '1 semana' },
    { value: '2 semanas', label: '2 semanas' }
  ]

  // Opções de garantia
  const garantiaOptions = [
    { value: '1 mês', label: '1 mês' },
    { value: '3 meses', label: '3 meses' },
    { value: '6 meses', label: '6 meses' },
    { value: '1 ano', label: '1 ano' }
  ]

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Cog className="w-6 h-6 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {servico ? 'Editar Serviço' : 'Novo Serviço'}
          </h3>
          <p className="text-sm text-gray-500">
            {servico ? 'Atualize as informações do serviço' : 'Preencha os dados do novo serviço'}
          </p>
        </div>
      </div>

      {/* Seção Principal */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <div className="flex items-center space-x-2 text-gray-700">
          <Wrench className="w-4 h-4" />
          <span className="font-medium">Informações Básicas</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Nome do Serviço"
            placeholder="Ex: Troca de Ecrã iPhone 14"
            {...getFieldProps('nome')}
            error={errors.nome}
            required
          />

          <Select
            label="Categoria"
            options={categoriasOptions}
            {...getFieldProps('categoria')}
            error={errors.categoria}
            required
          />
        </div>

        <TextArea
          label="Descrição"
          placeholder="Descreva detalhadamente o serviço..."
          rows={3}
          {...getFieldProps('descricao')}
          error={errors.descricao}
        />
      </div>

      {/* Seção Financeira e Tempos */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <div className="flex items-center space-x-2 text-gray-700">
          <Euro className="w-4 h-4" />
          <span className="font-medium">Preço e Tempos</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            label="Preço (€)"
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            {...getFieldProps('preco')}
            error={errors.preco}
            required
          />

          <Select
            label="Tempo Estimado"
            options={temposOptions}
            {...getFieldProps('tempoEstimado')}
            error={errors.tempoEstimado}
            required
          />

          <Select
            label="Período de Garantia"
            options={garantiaOptions}
            {...getFieldProps('garantia')}
            error={errors.garantia}
            required
          />
        </div>
      </div>

      {/* Equipamentos Suportados */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-gray-700">
            <Shield className="w-4 h-4" />
            <span className="font-medium">Equipamentos Suportados</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={adicionarEquipamento}
          >
            <Plus className="w-4 h-4 mr-1" />
            Adicionar
          </Button>
        </div>

        <div className="space-y-2">
          {equipamentos.map((equipamento, index) => (
            <div key={index} className="flex items-center space-x-2">
              <Input
                placeholder="Ex: iPhone 14, Samsung Galaxy S21..."
                value={equipamento}
                onChange={(e) => atualizarEquipamento(index, e.target.value)}
                className="flex-1"
              />
              {equipamentos.length > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removerEquipamento(index)}
                  className="text-red-600 hover:bg-red-50"
                >
                  <Minus className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Observações */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <TextArea
          label="Observações"
          placeholder="Notas adicionais sobre o serviço..."
          rows={3}
          {...getFieldProps('observacoes')}
          error={errors.observacoes}
        />
      </div>

      {/* Status */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={values.ativo}
            onChange={(e) => setFieldValue('ativo', e.target.checked)}
            className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
          />
          <span className="text-sm text-gray-700">Serviço ativo</span>
        </label>
      </div>

      {/* Buttons */}
      <div className="flex justify-end space-x-3 pt-6 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          isLoading={isSubmitting}
        >
          {servico ? 'Atualizar' : 'Criar'} Serviço
        </Button>
      </div>
    </form>
  )
}

export default ServicoForm